import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { toAbsoluteAssetUrl } from "@/hooks/useSiteSettings";
import { useAuth } from "@/context/AuthContext";
import { siteApi } from "@/lib/api";

import { StyledSitePopup } from "./styles";

const HIDDEN_UNTIL_STORAGE_KEY = "crypto_site_popup_hidden_until_v1";
const HIDE_FOR_MS = 24 * 60 * 60 * 1000;
const CLOSE_ANIMATION_MS = 180;

const readHiddenMap = () => {
  try {
    return JSON.parse(localStorage.getItem(HIDDEN_UNTIL_STORAGE_KEY) || "{}") || {};
  } catch (error) {
    return {};
  }
};

const writeHiddenMap = (hiddenMap) => {
  try {
    localStorage.setItem(HIDDEN_UNTIL_STORAGE_KEY, JSON.stringify(hiddenMap));
  } catch (error) {
    // Storage can be unavailable in private browsing. Closing still works for this session.
  }
};

const isHiddenForNow = (popupId) => {
  const hiddenUntil = Number(readHiddenMap()[popupId] || 0);
  return hiddenUntil > Date.now();
};

const hideForOneDay = (popupId) => {
  const hiddenMap = readHiddenMap();
  hiddenMap[popupId] = Date.now() + HIDE_FOR_MS;
  writeHiddenMap(hiddenMap);
};

const isEnabled = (popup = {}) => {
  const disabledValues = [false, 0, "0", "false", "FALSE", "N", "n", "INACTIVE", "inactive"];
  return ![
    popup.look,
    popup.is_use,
    popup.is_visible,
    popup.is_active,
    popup.enabled,
    popup.display,
    popup.status,
  ].some((value) => disabledValues.includes(value));
};

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (payload && typeof payload === "object") return [payload];
  return [];
};

const htmlToText = (html = "") => {
  if (!html) return "";
  if (typeof window === "undefined" || !window.DOMParser) {
    return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  const documentFromHtml = new window.DOMParser().parseFromString(String(html), "text/html");
  return documentFromHtml.body.textContent.replace(/\s+/g, " ").trim();
};

const extractImageFromHtml = (html = "") => {
  if (!html) return "";

  if (typeof window !== "undefined" && window.DOMParser) {
    const documentFromHtml = new window.DOMParser().parseFromString(String(html), "text/html");
    const image = documentFromHtml.querySelector("img[src]");
    return image?.getAttribute("src") || "";
  }

  return String(html).match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i)?.[1] || "";
};

const getDirectImage = (popup = {}) =>
  popup.mobile_image ||
  popup.mobileImage ||
  popup.pc_image ||
  popup.pcImage ||
  popup.popup_image ||
  popup.popupImage ||
  popup.image_url ||
  popup.imageUrl ||
  popup.image ||
  popup.upload_file ||
  popup.file ||
  "";

const getPopupId = (popup = {}, index = 0, image = "") =>
  String(
    popup.id ||
      popup.popup_id ||
      popup.popupId ||
      popup.timestamp ||
      popup.created_at ||
      popup.title ||
      image ||
      `popup-${index}`
  );

const normalizePopup = (popup = {}, index = 0) => {
  const content = String(popup.content || popup.description || popup.body || "");
  const image = toAbsoluteAssetUrl(getDirectImage(popup) || extractImageFromHtml(content));
  const id = getPopupId(popup, index, image);
  const text = htmlToText(content);

  if (!image && !text && !popup.title) return null;

  return {
    id,
    image,
    title: popup.title || popup.name || "",
    content,
    text,
    order: Number(popup.zindex ?? popup.z_index ?? popup.sequence ?? popup.order ?? index),
  };
};

const normalizePopups = (payload) =>
  getRows(payload)
    .filter(isEnabled)
    .map(normalizePopup)
    .filter(Boolean)
    .filter((popup) => !isHiddenForNow(popup.id))
    .sort((a, b) => a.order - b.order);

const preloadImages = (popups = []) => {
  const urls = popups.map((popup) => popup.image).filter(Boolean);
  if (!urls.length) return Promise.resolve();

  return Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve) => {
          const image = new Image();
          const timer = setTimeout(resolve, 2000);
          const done = () => { clearTimeout(timer); resolve(); };
          image.onload = done;
          image.onerror = done;
          image.src = url;
        })
    )
  );
};

const SitePopup = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { user } = useAuth({ optional: true });
  const authKey = user?.isAuthenticated ? `member:${user?.id || user?.profile?.id || ''}` : 'guest';
  const [popups, setPopups] = useState([]);
  const dismissed = useRef(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);

  // Existing popup fetch/state responsibility: refresh only at user-visible boundaries.
  useEffect(() => {
    setPopups([]);
    if (!isHome) return undefined;
    let active = true;
    let sequence = 0;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      const request = ++sequence;
      try {
        const response = await siteApi.getPopupSettings();
        if (!active || request !== sequence) return;
        const next = response?.success ? normalizePopups(response.data) : [];
        // Remove deleted/disabled rows before waiting for any replacement image.
        const ids = new Set(next.map(popup => popup.id));
        setPopups(current => current.filter(popup => ids.has(popup.id)));
        await preloadImages(next);
        if (!active || request !== sequence) return;
        setPopups(next.filter(popup => !dismissed.current.has(popup.id)));
      } catch {
        if (active && request === sequence) setPopups([]);
      }
    };
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [isHome, authKey]);

  useEffect(() => {
    if (!popups.length) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [popups.length]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  const visibleIndex = Math.min(activeIndex, popups.length - 1);
  const activePopup = popups[visibleIndex];

  const closeCurrent = useCallback(
    ({ hideToday = false } = {}) => {
      if (!activePopup || isClosing) return;
      if (hideToday) hideForOneDay(activePopup.id);
      dismissed.current.add(activePopup.id);

      setIsClosing(true);
      closeTimerRef.current = setTimeout(() => {
        setPopups((currentPopups) => {
          const nextPopups = currentPopups.filter((popup) => popup.id !== activePopup.id);
          setActiveIndex((currentIndex) => {
            if (!nextPopups.length) return 0;
            return Math.min(currentIndex, nextPopups.length - 1);
          });
          return nextPopups;
        });
        setIsClosing(false);
      }, CLOSE_ANIMATION_MS);
    },
    [activePopup, isClosing]
  );

  const goToPopup = useCallback(
    (index) => {
      if (isClosing) return;
      setActiveIndex(index);
    },
    [isClosing]
  );

  const movePopup = useCallback(
    (direction) => {
      if (isClosing || popups.length <= 1) return;
      setActiveIndex((currentIndex) => {
        const nextIndex = Math.min(currentIndex, popups.length - 1) + direction;
        if (nextIndex < 0) return popups.length - 1;
        if (nextIndex >= popups.length) return 0;
        return nextIndex;
      });
    },
    [isClosing, popups.length]
  );

  if (!isHome || !activePopup) return null;

  const content = (
    <StyledSitePopup
      className={isClosing ? "is-closing" : ""}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeCurrent();
      }}
    >
      <section
        className={`site-popup-card ${activePopup.image ? "is-image" : "is-text"}`}
        role="dialog"
        aria-modal="true"
        aria-label={activePopup.title || "사이트 팝업"}
      >
        {activePopup.image ? (
          <div className="site-popup-media">
            {popups.length > 1 ? (
              <>
                <button
                  type="button"
                  className="site-popup-arrow is-prev"
                  aria-label="이전 팝업"
                  onClick={() => movePopup(-1)}
                >
                  <span aria-hidden="true">‹</span>
                </button>
                <button
                  type="button"
                  className="site-popup-arrow is-next"
                  aria-label="다음 팝업"
                  onClick={() => movePopup(1)}
                >
                  <span aria-hidden="true">›</span>
                </button>
              </>
            ) : null}
            <figure className="site-popup-figure">
              <img src={activePopup.image} alt={activePopup.title || "사이트 팝업"} />
            </figure>
          </div>
        ) : (
          <div className="site-popup-text-panel">
            {activePopup.title ? <h2>{activePopup.title}</h2> : null}
            <div
              className="site-popup-html"
              dangerouslySetInnerHTML={{ __html: activePopup.content || activePopup.text }}
            />
          </div>
        )}

        <div className="site-popup-footer">
          <div className="site-popup-actions">
            <button type="button" className="site-popup-secondary" onClick={() => closeCurrent()}>
              닫기
            </button>
            <button
              type="button"
              className="site-popup-primary"
              onClick={() => closeCurrent({ hideToday: true })}
            >
              24시간 동안 닫기
            </button>
          </div>
        </div>

        {popups.length > 1 ? (
          <div className="site-popup-dots" aria-label="팝업 목록">
            {popups.map((popup, index) => (
              <button
                type="button"
                key={popup.id}
                className={index === visibleIndex ? "active" : ""}
                aria-label={`${index + 1}번째 팝업 보기`}
                onClick={() => goToPopup(index)}
              />
            ))}
          </div>
        ) : null}
      </section>
    </StyledSitePopup>
  );

  return createPortal(content, document.body);
};

export default SitePopup;
